/*
 * The native shell, and its absence.
 *
 * Every function here has to work three ways — inside the Android app, inside
 * the iOS app, and in a browser tab that has none of it — because the same
 * bundle is all three. So nothing throws: a plugin that is not there is a
 * no-op, and the caller never asks which platform it is on.
 *
 * That is a deliberate inversion of the usual `if (isNative)` sprinkled through
 * the UI. A component that has to know it is running natively will eventually
 * get it wrong in one place, and the failure is a crash on the customer's
 * phone rather than a missing buzz.
 */

import { App } from '@capacitor/app'
import { Camera, MediaTypeSelection } from '@capacitor/camera'
import { Capacitor } from '@capacitor/core'
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics'
import { Keyboard } from '@capacitor/keyboard'
import { Share } from '@capacitor/share'
import { StatusBar, Style } from '@capacitor/status-bar'

import { readableInk } from './theme'

const native = Capacitor.isNativePlatform()

function available(plugin: string): boolean {
  return Capacitor.isPluginAvailable(plugin)
}

/**
 * A tick under the finger when a choice is taken.
 *
 * This is most of what "feels native" actually is. A tap that only changes
 * pixels reads as a web page; the same tap with 10ms of haptic reads as an app,
 * and it costs one call. On the web it goes through navigator.vibrate, which
 * Android honours and iOS Safari ignores — no worse than silence either way.
 */
export function tap() {
  if (!available('Haptics')) return
  void Haptics.impact({ style: ImpactStyle.Light }).catch(() => {})
}

/** Heavier, for the one tap that commits — confirming a booking. */
export function commitTap() {
  if (!available('Haptics')) return
  void Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {})
}

/**
 * The outcome of something that took a round trip.
 *
 * Only for real outcomes — a booking confirmed, an upload refused. Firing this
 * on every step turns it into background noise and it stops meaning anything.
 */
export function outcome(kind: 'success' | 'warning' | 'error') {
  if (!available('Haptics')) return
  const type =
    kind === 'success'
      ? NotificationType.Success
      : kind === 'warning'
        ? NotificationType.Warning
        : NotificationType.Error
  void Haptics.notification({ type }).catch(() => {})
}

/**
 * Paint the status bar in the business's colour.
 *
 * Native only, and it has to be re-applied whenever the tenant resolves rather
 * than set once at boot — the accent is not known until the first API response.
 * `setOverlaysWebView(false)` keeps the bar as its own strip: overlaying it
 * would put the status text on top of the header, and Ionic's safe-area padding
 * already reserves the space for a non-overlaid bar.
 */
export async function applyStatusBar(accent: string) {
  if (!native || !available('StatusBar')) return
  try {
    await StatusBar.setOverlaysWebView({ overlay: false })
    await StatusBar.setBackgroundColor({ color: accent })
    // Light text on a dark accent, dark text on a light one — the same pairing
    // the header ink uses, so the bar and the toolbar under it agree.
    await StatusBar.setStyle({
      style: readableInk(accent) === '#ffffff' ? Style.Dark : Style.Light,
    })
  } catch {
    // An older WebView, or a platform without a status bar to set.
  }
}

/**
 * Keep the dock above the keyboard.
 *
 * The dock is where every answer is given, so a keyboard covering it makes the
 * app unusable rather than untidy. Capacitor's `resize: 'body'` handles the
 * native side; this adds a class the stylesheet can use to drop decoration that
 * only makes sense at full height, and it is also the web's only signal that a
 * keyboard exists at all.
 */
export function watchKeyboard(): () => void {
  if (!available('Keyboard')) return () => {}

  const on = () => document.body.classList.add('blooq-keyboard')
  const off = () => document.body.classList.remove('blooq-keyboard')

  const shown = Keyboard.addListener('keyboardWillShow', on)
  const hidden = Keyboard.addListener('keyboardWillHide', off)

  return () => {
    void shown.then((handle) => handle.remove()).catch(() => {})
    void hidden.then((handle) => handle.remove()).catch(() => {})
    off()
  }
}

/**
 * The detail carried by Ionic's `ionBackButton` event.
 *
 * Written out rather than imported from `@ionic/core`, which reaches this app
 * only as a transitive dependency of `@ionic/react` — importing it directly
 * would be depending on a package we do not declare, and it would break the
 * day Ionic changes how it vendors its core.
 */
interface BackButtonEventDetail {
  register(
    priority: number,
    handler: (processNextHandler: () => void) => void | Promise<void>,
  ): void
}

/**
 * Above Ionic's own navigation handler (0) and below its menu (99) and overlays
 * (100), so an open drawer or sheet closes before the conversation unwinds.
 *
 * Those two are registered by Ionic itself, and getting them for free is the
 * whole reason this goes through `ionBackButton` rather than through the
 * Capacitor App plugin's `backButton` event. Listening to that directly — which
 * this used to do — runs the app's handler ALONGSIDE Ionic's rather than after
 * it, so a back press with the history drawer open both closed the drawer and
 * stepped the booking backwards.
 */
const FLOW_BACK_PRIORITY = 10

/** Below everything, so it runs only when nothing else claimed the press. */
const LEAVE_PRIORITY = -100

/**
 * The hardware back button, and its equivalents.
 *
 * ONE event for three gestures, which is the point:
 *
 *   Android, native      the hardware button or the back gesture, bridged by
 *                        Capacitor into the same event.
 *   Android, PWA         the same gesture, delivered by the Close Watcher API —
 *                        see `experimentalCloseWatcher` in main.tsx.
 *   Any browser          the Escape key, which is a close request like any other.
 *
 * `handler` returns true when it consumed the press. Returning false hands on
 * to the next registered handler, and eventually to `leave()` below.
 *
 * A browser without the Close Watcher API emits nothing here at all — Ionic
 * says so plainly — and back keeps doing what it always did, which is leave the
 * page. That is a graceful floor rather than a fallback worth building.
 */
export function watchBackButton(handler: () => boolean): () => void {
  const onBackButton = (event: Event) => {
    const { detail } = event as CustomEvent<BackButtonEventDetail>

    detail.register(FLOW_BACK_PRIORITY, (processNextHandler) => {
      if (handler()) return
      processNextHandler()
    })

    detail.register(LEAVE_PRIORITY, () => leave())
  }

  /*
   * Registered on every event rather than once: `detail.register` is only
   * callable during the dispatch, because Ionic collects the handlers
   * synchronously and then walks them in priority order.
   */
  document.addEventListener('ionBackButton', onBackButton)
  return () => document.removeEventListener('ionBackButton', onBackButton)
}

/**
 * Nothing left to unwind.
 *
 * On a device this closes the app, which is what Android users expect from a
 * first screen and emphatically not what they expect from the middle of a
 * booking — the handler above is what keeps those two apart.
 *
 * IN A BROWSER IT DOES NOTHING, DELIBERATELY.
 *
 * A close request carries no clue as to what produced it. The Android back
 * gesture and the Escape key arrive here as the same event, and the two want
 * opposite things: "get me out of this app", and "close whatever is on top of
 * the page I am using". `history.back()` here would serve the first and, on
 * every desktop, navigate a customer off a half-finished booking because they
 * tapped Escape — which is the worse trade by a distance.
 *
 * There is no cost on the other side. An installed app is opened cold at its
 * own start_url with nothing behind it, so `history.back()` would have been a
 * no-op there anyway; and in a browser tab the back button and the tab switcher
 * are still right there, because the Close Watcher only ever intercepts a close
 * REQUEST, never the browser's own chrome. Nobody is trapped.
 */
function leave() {
  if (native) void App.exitApp()
}

/** True where the OS share sheet exists. The web has one too, in most browsers. */
export function canShare(): boolean {
  return available('Share')
}

/**
 * Hand the appointment to whatever the customer uses to remember things.
 *
 * On a phone that is usually their own WhatsApp — which is where a booking
 * reference is actually useful, and somewhere we cannot put it ourselves.
 */
export async function share(options: { title: string; text: string; url?: string }) {
  if (!canShare()) return
  try {
    await Share.share({ ...options, dialogTitle: options.title })
  } catch {
    // The customer dismissed the sheet, which is not an error.
  }
}

/** True when the OS gallery/camera picker is available instead of a file input. */
export function canPickImage(): boolean {
  return native && available('Camera')
}

/**
 * The payment screenshot, from the gallery.
 *
 * The gallery and NOT the camera, and not a prompt offering both. What is being
 * uploaded is a screenshot of a UPI app that took the payment thirty seconds
 * ago — it is already on the device, and the camera is an answer to a question
 * nobody asked. An extra "photo or gallery?" sheet in front of that is one more
 * tap between a customer and a booking they have already paid for.
 *
 * Returns a Blob, so the caller posts the same shape whether the bytes came
 * from here or from a file input on the web.
 */
export async function pickImage(): Promise<Blob | null> {
  if (!canPickImage()) return null
  try {
    const picked = await Camera.chooseFromGallery({
      mediaType: MediaTypeSelection.Photo,
      allowMultipleSelection: false,
    })

    const first = picked.results[0]
    // `uri` on a device, `webPath` in a browser; neither is always present, and
    // a result with no path at all is a cancelled pick.
    const path = first?.webPath ?? first?.uri
    if (!path) return null

    return await (await fetch(path)).blob()
  } catch {
    // Cancelled, or permission refused. Both mean "no file", and the dock
    // already offers paying in person.
    return null
  }
}
